import { type IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { animated, useSpring } from "@react-spring/web";
import { cva, type VariantProps } from "class-variance-authority";
import { type PropsWithChildren } from "react";
import { clsx } from "clsx";

const modalButtonStyles = cva(
  "flex h-32 w-full items-center justify-center gap-x-4 overflow-hidden rounded-3xl px-8 py-6 backdrop-blur-sm",
  {
    variants: {
      variant: {
        success:
          "bg-kiosk-background-success-alt/50 font-normal text-kiosk-text-primary",
        idle: "bg-kiosk-text-white/15 font-semibold text-kiosk-text-white",
        transparent:
          "border border-kiosk-text-white font-semibold text-kiosk-text-white",
        error:
          "bg-kiosk-text-error/30 font-normal text-kiosk-background-primary"
      },
      size: {
        medium: "text-4xl",
        large: "text-5xl"
      }
    },
    defaultVariants: {
      variant: "success",
      size: "large"
    }
  }
);

const iconStyles = cva("", {
  variants: {
    variant: {
      success: "text-kiosk-text-primary",
      idle: "text-kiosk-text-white",
      transparent: "text-kiosk-text-white",
      error: "text-kiosk-background-primary"
    }
  },
  defaultVariants: {
    variant: "success"
  }
});

const overlayStyles = cva("absolute inset-y-0 left-0 h-full rounded-l-3xl", {
  variants: {
    variant: {
      success: "bg-kiosk-background-success-alt",
      idle: "bg-kiosk-text-white/15",
      transparent: "bg-kiosk-text-white/15",
      error: undefined
    }
  }
});

type ModalButtonProps = VariantProps<typeof modalButtonStyles> &
  PropsWithChildren<{
    onClick?: () => void;
    icon?: IconDefinition;
    className?: string;
    timeoutDuration?: number;
    hidden?: boolean;
    type?: "button" | "submit";
    disabled?: boolean;
  }>;

/**
 * Properties for the ModalButton component.
 * @typedef {Object} ModalButtonProps
 * @property {() => void} onClick - Callback function to be invoked when the button is clicked.
 * @property {IconDefinition} [icon] - Optional FontAwesome icon to be displayed within the button.
 * @property {string} [className] - Optional additional class names to be applied to the button.
 * @property {number} [timeoutDuration] - Optional duration for the animation in milliseconds. The onClick function will be called when the animation ends.
 * @property {"button" | "submit"} [type] - Optional type for the button.
 * @property {boolean} [disabled] - Optional disabled state for the button.
 * @property {boolean} [hidden] - Optional hidden state for the button.
 */
export const ModalButton = ({
  children,
  onClick,
  variant,
  timeoutDuration,
  icon,
  size,
  className,
  hidden = false,
  disabled = false,
  type = "button"
}: ModalButtonProps) => {
  const animationProps = useSpring(
    timeoutDuration
      ? {
          from: { width: "0%" },
          to: { width: "100%" },
          config: {
            duration: timeoutDuration,
            friction: 120,
            tension: 120
          },
          onRest(result) {
            if (result.finished) {
              onClick?.();
            }
          }
        }
      : {}
  );

  return (
    <button
      type={type === "button" ? "button" : "submit"}
      className={modalButtonStyles({
        variant,
        size,
        className: clsx(className, {
          hidden
        })
      })}
      onClick={onClick}
      disabled={disabled}
    >
      {timeoutDuration ? (
        <animated.div
          className={overlayStyles({ variant })}
          style={{ ...animationProps }}
        />
      ) : null}
      <div className="z-10 flex items-center justify-center gap-x-4">
        {icon ? (
          <FontAwesomeIcon
            icon={icon}
            fontSize={36}
            className={iconStyles({ variant })}
          />
        ) : null}
        {children}
      </div>
    </button>
  );
};
